import { Color } from '../math/Color';
import { Rect } from '../math/Rect';
import { Vector2 } from '../math/Vector2';

const MAX_DECIMALS = 3;
const MAX_LIST_ITEMS = 8;
const MAX_DEPTH = 1;
const TRAILING_ZEROS = /\.?0+$/;

/**
 * Renders any component field as one short line of text.
 *
 * This function is why no per-type inspector exists. Components are pure data, so a snapshot only ever
 * has to turn plain fields into something readable, and the handful of Kite value types get a compact
 * form here instead of every one of them growing a `toString`.
 *
 * Output is for a human reading a debug panel, not for round-tripping: values are rounded, long lists
 * are elided and nesting stops one level down.
 */
export function formatDebugValue(value: unknown): string {
  return formatValue(value, 0);
}

function formatValue(value: unknown, depth: number): string {
  if (value === undefined) {
    return 'undefined';
  }
  if (value === null) {
    return 'null';
  }

  switch (typeof value) {
    case 'number':
      return formatNumber(value);
    case 'string':
      return value;
    case 'boolean':
      return value ? 'true' : 'false';
    case 'bigint':
      return `${value.toString()}n`;
    case 'function':
      return `${value.name === '' ? 'anonymous' : value.name}()`;
    case 'symbol':
      return value.toString();
    default:
      return formatReference(value, depth);
  }
}

function formatReference(value: object, depth: number): string {
  if (value instanceof Vector2) {
    return `(${formatNumber(value.x)}, ${formatNumber(value.y)})`;
  }
  if (value instanceof Color) {
    return value.toHex();
  }
  if (value instanceof Rect) {
    const corner = `(${formatNumber(value.x)}, ${formatNumber(value.y)})`;
    return `${corner} ${formatNumber(value.width)}x${formatNumber(value.height)}`;
  }
  if (Array.isArray(value)) {
    return formatList(value as readonly unknown[], depth, '[', ']');
  }
  if (value instanceof Set) {
    return formatList([...(value as ReadonlySet<unknown>)], depth, '{', '}');
  }
  if (value instanceof Map) {
    return `Map(${formatNumber(value.size)})`;
  }
  return formatObject(value, depth);
}

function formatObject(value: object, depth: number): string {
  const typeName = value.constructor.name;
  const prototype = Object.getPrototypeOf(value) as object | null;
  const isPlain = prototype === null || prototype === Object.prototype;

  if (!isPlain) {
    return typeName;
  }
  if (depth >= MAX_DEPTH) {
    return '{...}';
  }

  const fields = Object.entries(value as Record<string, unknown>).map(
    ([name, field]) => `${name}: ${formatValue(field, depth + 1)}`,
  );
  return fields.length === 0 ? '{}' : `{ ${fields.join(', ')} }`;
}

function formatList(items: readonly unknown[], depth: number, open: string, close: string): string {
  if (items.length === 0) {
    return `${open}${close}`;
  }
  if (depth >= MAX_DEPTH) {
    return `${open}${formatNumber(items.length)} items${close}`;
  }

  const shown = items.slice(0, MAX_LIST_ITEMS).map((item) => formatValue(item, depth + 1));
  const elided = items.length - shown.length;
  if (elided > 0) {
    shown.push(`+${formatNumber(elided)} more`);
  }
  return `${open}${shown.join(', ')}${close}`;
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return value.toString();
  }
  if (Number.isInteger(value)) {
    return Object.is(value, -0) ? '0' : value.toString();
  }
  return value.toFixed(MAX_DECIMALS).replace(TRAILING_ZEROS, '');
}
